import LegalDocument from "../components/LegalDocument";
import usePageMeta from "../hooks/usePageMeta";

const UPDATED = "28 June 2026";

const INTRO = (
    <>
        <p>
            Strata Roof Management Pty Ltd provides independent roof
            assessment, condition reporting, project management and long-term
            roof asset management services throughout Australia. We respect
            the privacy of every committee member, building manager, asset
            owner and individual we engage with — and this policy explains
            how we collect, hold, use and disclose personal information.
        </p>
        <p>
            This Privacy Policy is published in accordance with the Australian
            Privacy Act 1988 (Cth) and the thirteen Australian Privacy
            Principles (APPs). It applies to all information collected
            through this website (
            <a
                href="https://strataroofmanagement.com.au"
                className="text-srm-navy font-semibold underline-offset-4 hover:underline"
            >
                strataroofmanagement.com.au
            </a>
            ), our enquiry and engagement forms, our inspection and reporting
            workflows, and any direct correspondence with our team.
        </p>
    </>
);

const SECTIONS = [
    {
        id: "info-we-collect",
        heading: "Information we collect",
        body: (
            <>
                <p>
                    We collect personal information that is reasonably
                    necessary for, and directly related to, providing
                    independent roof asset management services. This includes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        Contact details — full name, organisation, role
                        (committee member, strata manager, building manager,
                        owner), phone number and email address.
                    </li>
                    <li>
                        Property details — site address, building type and
                        access information needed to perform an assessment.
                    </li>
                    <li>
                        Engagement details — the nature of your enquiry, any
                        existing roof condition information you share, and any
                        documents you upload as part of an inspection.
                    </li>
                    <li>
                        Website usage data — pages visited, referring site,
                        device and browser type, approximate location (city
                        level), collected via Google Analytics 4 for the sole
                        purpose of improving the website.
                    </li>
                </ul>
                <p>
                    We do not collect sensitive information (as defined under
                    APP 3) unless it is necessary for a specific engagement,
                    and where required we will request your express consent
                    before collection.
                </p>
            </>
        ),
    },
    {
        id: "how-we-use",
        heading: "How we use your information",
        body: (
            <>
                <p>
                    Information is used exclusively to provide and improve our
                    services. Specifically:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        To respond to enquiries and schedule independent roof
                        condition assessments, insurance investigations and
                        compliance reporting.
                    </li>
                    <li>
                        To produce, review and deliver written inspection
                        reports and capital works planning documents.
                    </li>
                    <li>
                        To communicate with you about the progress of an
                        engagement, follow-up advice and scheduled
                        re-assessments under the Guardian Program\u2122.
                    </li>
                    <li>
                        To meet record-keeping, tax and insurance obligations
                        applicable to a professional consultancy.
                    </li>
                    <li>
                        To improve our website and content (aggregated
                        analytics only — no individual tracking).
                    </li>
                </ul>
                <p>
                    We do not sell personal information. We do not use your
                    information for unrelated marketing purposes. We do not
                    share enquiry data with roofing contractors, suppliers or
                    any commission-linked third party.
                </p>
            </>
        ),
    },
    {
        id: "disclosure",
        heading: "Disclosure to third parties",
        body: (
            <>
                <p>
                    Personal information may be disclosed only to the limited
                    extent necessary to deliver the service you have engaged
                    us for, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        Engaged service providers (e.g. drone operators or
                        rope-access specialists) who attend a site under a
                        contractual confidentiality obligation to SRM.
                    </li>
                    <li>
                        Insurers, loss adjusters or tribunals where a report
                        is commissioned specifically for an insurance claim or
                        dispute and you have authorised that disclosure.
                    </li>
                    <li>
                        Government authorities where legally compelled to do
                        so (e.g. lawful subpoena, court order, statutory
                        request).
                    </li>
                </ul>
                <p>
                    We do not transfer personal information overseas without
                    your knowledge, except where data is incidentally stored on
                    Australian-region cloud services with appropriate
                    contractual safeguards.
                </p>
            </>
        ),
    },
    {
        id: "data-security",
        heading: "Data security",
        body: (
            <>
                <p>
                    We take reasonable administrative, technical and physical
                    safeguards to protect personal information from misuse,
                    interference, loss, unauthorised access, modification or
                    disclosure.
                </p>
                <p className="font-semibold text-srm-navy">
                    Administrative safeguards
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        Written confidentiality and data-handling obligations
                        for every SRM employee, contractor and engaged service
                        provider.
                    </li>
                    <li>
                        Role-based access — only personnel directly involved
                        in delivering your engagement can access your file.
                    </li>
                    <li>
                        Periodic privacy and information-security training.
                    </li>
                    <li>
                        A documented data-breach response process aligned to
                        the Notifiable Data Breaches scheme under the Privacy
                        Act.
                    </li>
                </ul>
                <p className="font-semibold text-srm-navy">
                    Technical safeguards
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        TLS encryption for all data in transit between your
                        browser and our systems.
                    </li>
                    <li>
                        Encrypted storage for files at rest within reputable
                        Australian-region cloud providers.
                    </li>
                    <li>
                        Multi-factor authentication on all administrative
                        access to client data.
                    </li>
                    <li>
                        Independent backups with retention limits aligned to
                        legal and professional record-keeping obligations.
                    </li>
                </ul>
                <p className="font-semibold text-srm-navy">
                    Physical safeguards
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        Secure storage of any printed inspection materials at
                        controlled-access premises.
                    </li>
                    <li>
                        Secure destruction of any physical records when no
                        longer required for legal or professional purposes.
                    </li>
                </ul>
            </>
        ),
    },
    {
        id: "cookies-analytics",
        heading: "Cookies and analytics",
        body: (
            <>
                <p>
                    Our website uses cookies and similar technologies to
                    operate the site, remember your preferences and measure
                    aggregated usage. We use Google Analytics 4 to understand
                    how visitors interact with the site so that we can
                    continually improve content and structure.
                </p>
                <p>
                    Google Analytics 4 collects anonymised usage data
                    (pageviews, session duration, device class, approximate
                    region) and does not identify individual visitors to SRM.
                    You can opt out of Google Analytics tracking by installing
                    the Google Analytics Opt-out Browser Add-on, or by
                    disabling third-party cookies in your browser.
                </p>
                <p>
                    Most browsers allow you to view, manage, delete and block
                    cookies for any website. Refer to your browser&apos;s
                    documentation for instructions.
                </p>
            </>
        ),
    },
    {
        id: "your-rights",
        heading: "Your rights",
        body: (
            <>
                <p>
                    Under the Australian Privacy Principles you have the
                    right to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        Request access to the personal information we hold
                        about you.
                    </li>
                    <li>
                        Request correction of information that is inaccurate,
                        out-of-date, incomplete, irrelevant or misleading.
                    </li>
                    <li>
                        Request deletion of personal information that is no
                        longer required to be retained for legal, professional
                        or contractual reasons.
                    </li>
                    <li>
                        Opt out of any future direct communications by
                        contacting us using the details below.
                    </li>
                    <li>
                        Make a complaint about how we have handled your
                        personal information.
                    </li>
                </ul>
                <p>
                    If you are not satisfied with our response to a complaint,
                    you may escalate the matter to the Office of the
                    Australian Information Commissioner (OAIC) at{" "}
                    <a
                        href="https://www.oaic.gov.au"
                        className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                    >
                        oaic.gov.au
                    </a>
                    .
                </p>
            </>
        ),
    },
    {
        id: "retention",
        heading: "Data retention",
        body: (
            <>
                <p>
                    We retain personal information only for as long as is
                    necessary to deliver the engaged service, satisfy our
                    legal and professional record-keeping obligations
                    (typically up to seven years for engagement records) and
                    support any related insurance or dispute resolution
                    process.
                </p>
                <p>
                    When information is no longer required, it is securely
                    destroyed or permanently de-identified.
                </p>
            </>
        ),
    },
    {
        id: "changes",
        heading: "Changes to this policy",
        body: (
            <>
                <p>
                    We may update this Privacy Policy from time to time to
                    reflect changes in our practices, technology, legal
                    requirements or services. The current version is always
                    available on this page. The &quot;Last updated&quot; date at the
                    top of the document indicates when the most recent
                    revision was published.
                </p>
            </>
        ),
    },
    {
        id: "contact",
        heading: "How to contact us",
        body: (
            <>
                <p>
                    For any privacy-related question, request or complaint,
                    please contact:
                </p>
                <div className="mt-4 rounded-md border border-srm-line bg-white p-6 space-y-2">
                    <p className="font-display font-bold text-srm-navy">
                        Strata Roof Management Pty Ltd — Privacy Officer
                    </p>
                    <p>
                        Email:{" "}
                        <a
                            href="mailto:info@strataroofmanagement.com.au"
                            className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                        >
                            info@strataroofmanagement.com.au
                        </a>
                    </p>
                    <p>
                        Phone:{" "}
                        <a
                            href="tel:+61407053267"
                            className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                        >
                            0407 053 267
                        </a>
                    </p>
                    <p className="text-[14px] text-srm-body/85">
                        Sunshine Coast, Queensland, Australia · Service area:
                        Australia-wide
                    </p>
                </div>
            </>
        ),
    },
];

export default function Privacy() {
    usePageMeta({
        title: "Privacy Policy — SRM",
        description:
            "How Strata Roof Management Pty Ltd collects, holds, uses and protects personal information under the Australian Privacy Act 1988 (Cth).",
        path: "/privacy",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Privacy Policy", url: "/privacy" },
        ],
    });

    return (
        <LegalDocument
            eyebrow="Legal"
            title="Privacy Policy"
            updated={UPDATED}
            intro={INTRO}
            sections={SECTIONS}
            testIdPrefix="privacy"
        />
    );
}
